// services/holidays.js — работни и почивни дни в България (КТ, чл. 154).
//
// ЧИСТИ функции: не пипат базата и не четат средата. Разместените почивни
// дни по решение на Министерския съвет НЕ могат да се изчислят — подават се
// отвън като списък „overrides" и ВИНАГИ имат приоритет:
//   [{ date: 'YYYY-MM-DD', kind: 'working' | 'nonworking', reason: '…' }]
//
// Дати навсякъде са низове 'YYYY-MM-DD' (както в цялата система). Сметките
// вървят в UTC, за да не зависят от часовата зона на сървъра.

const DAY_MS = 24 * 60 * 60 * 1000;

// Официалните празници с фиксирана дата (чл. 154, ал. 1).
// 1 ноември (Ден на народните будители) НЕ е тук — той е неприсъствен само
// за учебните заведения, не е общ почивен ден.
const FIXED = [
  ['01-01', 'Нова година'],
  ['03-03', 'Ден на Освобождението на България'],
  ['05-01', 'Ден на труда'],
  ['05-06', 'Гергьовден, Ден на храбростта и на Българската армия'],
  ['05-24', 'Ден на светите братя Кирил и Методий, на българската азбука, просвета и култура'],
  ['09-06', 'Ден на Съединението'],
  ['09-22', 'Ден на Независимостта на България'],
  ['12-24', 'Бъдни вечер'],
  ['12-25', 'Рождество Христово'],
  ['12-26', 'Рождество Христово'],
];

function pad2(n) { return (n < 10 ? '0' : '') + n; }
function fmt(d) { return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate()); }
function parse(iso) {
  const s = String(iso || '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) throw new Error('Невалидна дата: ' + iso);
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  // Date.UTC ПРЕЛИВА (31 февруари става 3 март), затова сверяваме обратно —
  // иначе несъществуващи дати биха се приемали за валидни.
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
    throw new Error('Невалидна дата: ' + iso);
  }
  return dt;
}

// Дали низът е съществуваща календарна дата (без да хвърля).
function isValidDate(iso) {
  try { parse(iso); return true; } catch (e) { return false; }
}
function addDays(iso, n) { return fmt(new Date(parse(iso).getTime() + n * DAY_MS)); }
function isWeekend(iso) { const w = parse(iso).getUTCDay(); return w === 0 || w === 6; }

// Православната Пасха (Великден). Юлиански computus по Meeus, след което
// датата се преобразува към григорианския календар (+13 дни за 1900–2099).
// НЕ е католическата Пасха — тя се смята по друг алгоритъм.
function orthodoxEaster(year) {
  const y = Number(year);
  const a = y % 4;
  const b = y % 7;
  const c = y % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31); // 3 = март, 4 = април
  const day = ((d + e + 114) % 31) + 1;
  // Датата дотук е по ЮЛИАНСКИЯ календар — привеждаме я към григорианския.
  const julian = Date.UTC(y, month - 1, day);
  const shift = julianToGregorianShift(y);
  return fmt(new Date(julian + shift * DAY_MS));
}

// Разликата между двата календара в дни (13 за целия XX и XXI век).
function julianToGregorianShift(year) {
  const c = Math.floor(year / 100);
  return c - Math.floor(c / 4) - 2;
}

// Великденските празници: Разпети петък, Велика събота, Великден (неделя)
// и Велики понеделник. Те НЕ се пренасят, дори да съвпаднат с почивен ден.
function easterHolidays(year) {
  const sunday = orthodoxEaster(year);
  return [
    [addDays(sunday, -2), 'Разпети петък'],
    [addDays(sunday, -1), 'Велика събота'],
    [sunday, 'Великден'],
    [addDays(sunday, 1), 'Велики понеделник'],
  ];
}

function normalizeOverrides(overrides) {
  const map = new Map();
  for (const o of overrides || []) {
    if (!o || !o.date) continue;
    const date = String(o.date).slice(0, 10);
    const kind = o.kind === 'working' ? 'working' : 'nonworking';
    map.set(date, { kind, reason: String(o.reason || '') });
  }
  return map;
}

// Всички неприсъствени дни за годината: празници + пренесени дни.
// Връща подреден списък [{ date, name, moved }], където moved=true значи
// „почивен ден, пренесен заради празник в събота/неделя".
// overrides добавя/маха дни (kind 'nonworking' / 'working').
function holidaysForYear(year, overrides) {
  const y = Number(year);
  if (!Number.isInteger(y) || y < 1970 || y > 2200) throw new Error('Невалидна година: ' + year);
  const byDate = new Map();
  // Ако два празника съвпадат (напр. 1 май 2027 е и Велика събота), ИМЕТО
  // събира двата — иначе вторият изчезва от екрана и от ICS-а.
  const add = (date, name, moved) => {
    const cur = byDate.get(date);
    if (!cur) { byDate.set(date, { date, name, moved: !!moved }); return; }
    if (cur.name !== name && !cur.name.includes(name)) cur.name += ' / ' + name;
  };

  const fixedDates = [];
  for (const [md, name] of FIXED) {
    const date = y + '-' + md;
    add(date, name, false);
    fixedDates.push([date, name]);
  }
  for (const [date, name] of easterHolidays(y)) add(date, name, false);

  // Пренасяне (чл. 154, ал. 2): празник в събота/неделя → следващият един
  // или два РАБОТНИ дни стават неприсъствени. Великденските са изключение,
  // затова тръгваме само от фиксираните празници.
  for (const [date, name] of fixedDates) {
    if (!isWeekend(date)) continue;
    let cur = addDays(date, 1);
    // Първият ден, който още не е зает (не е събота/неделя, не е празник и
    // не е вече пренесен почивен ден).
    while (isWeekend(cur) || byDate.has(cur)) cur = addDays(cur, 1);
    add(cur, 'Почивен ден (пренесен от ' + name + ')', true);
  }

  const ov = normalizeOverrides(overrides);
  for (const [date, o] of ov) {
    if (String(date).slice(0, 4) !== String(y)) continue;
    if (o.kind === 'working') byDate.delete(date);
    else add(date, o.reason || 'Неприсъствен ден (решение на МС)', true);
  }

  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

// Празник ли е денят (или пренесен почивен ден). Събота/неделя НЕ са празници.
// Връща името на празника или '' — така извикващият може и да го покаже.
function holidayName(date, overrides) {
  const iso = String(date).slice(0, 10);
  const ov = normalizeOverrides(overrides).get(iso);
  if (ov && ov.kind === 'working') return '';
  const hit = holidaysForYear(iso.slice(0, 4), overrides).find((h) => h.date === iso);
  return hit ? hit.name : '';
}

function isHoliday(date, overrides) { return holidayName(date, overrides) !== ''; }

// Работен ден = не е събота/неделя и не е празник. Override с kind='working'
// прави деня работен дори да е събота (така МС „разменя" дните).
function isWorkingDay(date, overrides) {
  const iso = String(date).slice(0, 10);
  const ov = normalizeOverrides(overrides).get(iso);
  if (ov && ov.kind === 'working') return true;
  if (ov && ov.kind === 'nonworking') return false;
  if (isWeekend(iso)) return false;
  return !isHoliday(iso, overrides);
}

// Следващият работен ден СЛЕД подадената дата (самата дата не се брои).
// Оттук се ползва и за срокове: падеж в неработен ден се мести напред.
function nextWorkingDay(date, overrides) {
  let cur = addDays(date, 1);
  let guard = 0;
  while (!isWorkingDay(cur, overrides)) {
    cur = addDays(cur, 1);
    if (++guard > 60) throw new Error('Няма работен ден в следващите 60 дни — проверете overrides.');
  }
  return cur;
}

// Брой работни дни в интервала, ВКЛЮЧИТЕЛНО двата края.
// workingDaysBetween('2026-01-05', '2026-01-09') = 5 (понеделник–петък).
// Обърнат интервал (край преди начало) е 0, НЕ тиха размяна (преглед
// 17.09.2026): размяната караше сметки за обезщетения да ПЛАЩАТ за
// отрицателен период — services/severance.js се пазеше сам с коментар
// „фантомен таван", а другите викащи не.
function workingDaysBetween(from, to, overrides) {
  const a = String(from).slice(0, 10);
  const b = String(to).slice(0, 10);
  if (!a || !b || a > b) return 0;
  let n = 0;
  for (let cur = a; cur <= b; cur = addDays(cur, 1)) if (isWorkingDay(cur, overrides)) n++;
  return n;
}

module.exports = {
  holidaysForYear,
  holidayName,
  isValidDate,
  isHoliday,
  isWorkingDay,
  nextWorkingDay,
  workingDaysBetween,
  orthodoxEaster,
  // помощните са изнесени за други services (напр. календара)
  addDays,
  isWeekend,
};
